/*
 * Copyright (c) 2024. Dave van Rijn Development
 */
import './style/pdfImage.scss';

import React, {MouseEventHandler} from 'react';
import {
    ElementPosition, IndexedObject, PDFDisplay, PDFElementParams, PDFElementPersist, PDFElementType, PDFImageParams
} from "../../util/interfaces";
import {PDFDraggable, PdfElement} from "../element/pdfElement";
import {nullify} from "../../util/miscUtil";
import merge from 'lodash.merge';
import {AwesomeIcon, directTimeout, PAGE_HEIGHT, PAGE_WIDTH} from "../../../../index";

interface Props {
    onDelete: MouseEventHandler;
    onSaveHistory: VoidFunction;
    values: IndexedObject<string>;
    defaultSettings?: Partial<PDFImageParams>;
    id?: string;
    defaultPosition: { x: number; y: number };
    draggable: boolean;
    onFocusElement: (element: PdfElement) => void;
    width?: number;
    height?: number;
    persistent?: PDFElementPersist;
}

interface State {
    dragging: boolean;
    settings: { img: string; persistent: PDFElementPersist; alignment?: ElementPosition;  display?: PDFDisplay };
}

export default class PdfImage extends PdfElement<Props, State> {
    static defaultProps = {
        defaultPosition: {x: 0, y: 0},
    };

    private input: HTMLInputElement;
    private image: File | null = null;

    constructor(props: Props) {
        super(props);
        this.state = {
            settings: merge({}, {
                img: props.values?.default_image || '',
                persistent: PDFElementPersist.NOT_PERSISTENT,
                alignment: undefined,
                display: PDFDisplay.ALL_PAGES,
            }, props.defaultSettings),
            dragging: false,
        };
    };

    public onChooseFile = () => {
        if (this.input)
            this.input.click();
    };

    onChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
        const {files} = evt.target;
        if (files?.length) this.setWidth(files[0]);
    };

    onClick = (evt: React.MouseEvent): void => {
        evt.stopPropagation();
        this.props.onFocusElement(this);
        if (this.draggable)
            this.draggable.setFocus();
    }

    onClickDelete = (evt: React.MouseEvent): void => {
        evt.stopPropagation();
        const {onDelete} = this.props;
        onDelete(evt);
    };

    onClickBar = (evt: React.MouseEvent) => {
        this.onClick(evt);
    };

    setWidth = (file: File | string, setDirect: boolean = false) => {
        const img: HTMLImageElement = new Image;
        img.onload = () => {
            const MAX_WIDTH = PAGE_WIDTH, MAX_HEIGHT = PAGE_HEIGHT, {width, height} = img;
            let calcWidth = this.props.width ?? width, calcHeight = this.props.height ?? height;
            const widthDiff = calcWidth - MAX_WIDTH, heightDiff = calcHeight - MAX_HEIGHT,
                imgRatio = height / width, calcRatio = calcHeight / calcWidth;
            if (calcRatio != imgRatio) calcHeight = calcHeight / calcRatio * imgRatio;
            let finalWidth: number = calcWidth, finalHeight: number = calcHeight;
            if (widthDiff > 0 || heightDiff > 0) {
                if (widthDiff > heightDiff) {
                    finalWidth = MAX_WIDTH;
                    finalHeight = Math.round(finalWidth * imgRatio);
                } else {
                    finalHeight = MAX_HEIGHT;
                    finalWidth = Math.round(finalHeight / imgRatio);
                }
            }
            if (this.draggable)
                this.draggable.setSize(finalWidth, finalHeight, !setDirect);
        };
        let imageSource: string;
        if (typeof file === 'string') {
            imageSource = file;
            img.src = file;
        } else {
            imageSource = URL.createObjectURL(file);
            if (!setDirect)
                img.src = imageSource
            this.image = file;
        }
        if (setDirect) {
            this.setState({settings: Object.assign({}, this.state.settings, {img: imageSource})});
        } else this.setSetting('img', img.src);
    };

    getParams = (convertPixels: boolean = true): PDFElementParams<PDFElementType.IMAGE, PDFImageParams> => {
        const {settings} = this.state;
        return {
            key: this.id,
            dimensions: this.getDimensions(convertPixels),
            type: PDFElementType.IMAGE,
            options: {
                img: this.image ?? nullify(settings.img),
                persistent: settings.persistent,
                alignment: settings.alignment,
                display: settings.display,
            },
        };
    };

    isDraggable = (): boolean => this.state.settings.alignment === undefined && this.props.draggable;

    renderImg = () => {
        const {img} = this.state.settings;
        if (img) return <img src={img} alt='Afbeelding niet gevonden' className='image'/>;
        return (
            <div className='img-selector'>
                <AwesomeIcon name='image' className='img-icon'/>
                <span className='img-selector-label'>Kies afbeelding</span>
            </div>
        )
    };

    componentDidMount = () => {
        const {settings} = this.state;
        directTimeout(() => {
            this.setWidth(settings.img, true);
        });
    };

    render = () => {
        const {settings, dragging} = this.state, {defaultPosition, width, height, onSaveHistory, onDelete, persistent} = this.props;
        return (
            <PDFDraggable onDragEnd={this.onDragEnd} elementLabel='Afbeelding' onDragStart={this.onDragStart}
                          draggable={this.isDraggable()} dragging={dragging} position={defaultPosition}
                          maxWidth={PAGE_WIDTH} maxHeight={PAGE_HEIGHT} width={width} height={height} lockAspect
                          onDelete={onDelete} onEdit={this.onClick} onClickBar={this.onClickBar} persistent={persistent}
                          ref={(ref: PDFDraggable) => {
                              this.draggable = ref
                          }} matchParentSize forwardRef={(ref: HTMLDivElement) => {
                this.element = ref
            }} onDragged={onSaveHistory} onResized={onSaveHistory} alignment={settings.alignment}>
                <div className='pdf-image' onClick={this.onClick}>
                    {this.renderImg()}
                    <input type='file' accept='image/png, image/jpeg' onChange={this.onChange}
                           style={{display: 'none'}} ref={(ref: HTMLInputElement) => {
                        this.input = ref
                    }}/>
                </div>
            </PDFDraggable>
        )
    };
}